import React from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import overrideStyle from 'lib/overrideStyle';

import { ICON_TYPES } from 'enums/configuration';
import { shiftColorShade } from 'lib/colorLogic.js';
import { trackQuickActionSelected } from 'events/analytics';
import CustomIcon from 'presentation/components/icons/CustomIcon';
import getRadius from 'lib/getRadius';
import { isIE } from 'lib/getCustomerProperties';
import selfServiceEnums from 'enums/selfService';
import updateSelectedAnswer from 'actions/selfService/updateSelectedAnswer';

class QuickActionItem extends React.Component {
  constructor(props) {
    super(props);

    this.selectItem = this.selectItem.bind(this);
    this.handleKeyDown = this.handleKeyDown.bind(this);
    this.isInternal = this.isInternal.bind(this);
  }

  isInternal() {
    const answer = this.props.answer;
    if (answer) {
      return answer.content.type === selfServiceEnums.quickActionTypes.INTERNAL;
    }
    return false;
  }

  selectItem() {
    const answer = this.props.answer;
    const entityType = this.isInternal() ? 'answer' : 'externalLink';
    this.props.onSelectQuickAction({
      entityId: answer.content.value,
      entityType,
      label: answer.name,
      position: this.props.position,
    });
  }

  handleKeyDown(ev) {
    if (ev.key === 'Enter') {
      this.selectItem();
    }
  }

  renderLinkIcon() {
    return (
      <StyledQuickActionItemLink>
        <CustomIcon type={this.isInternal() ? ICON_TYPES.INTERNAL_LINK : ICON_TYPES.EXTERNAL_LINK} />
      </StyledQuickActionItemLink>
    );
  }

  renderItemText(name) {
    if (isIE()) {
      return <StyledQuickActionItemTextIE11>{name}</StyledQuickActionItemTextIE11>;
    } else {
      return <StyledQuickActionItemText>{name}</StyledQuickActionItemText>;
    }
  }

  render() {
    const answer = this.props.answer;
    const href = this.isInternal() ? null : answer.content.value;
    return (
      <StyledQuickActionItem
        aria-label={`${answer.name} ${this.isInternal() ? 'quick action answer' : 'opens in a new window'}`}
        href={href}
        onKeyDown={this.handleKeyDown}
        onMouseDown={this.selectItem}
        rel="noopener noreferrer"
        tabIndex="0"
        target="_blank"
      >
        {this.renderItemText(answer.name)}
        {this.renderLinkIcon()}
      </StyledQuickActionItem>
    );
  }
}

QuickActionItem.propTypes = {
  answer: PropTypes.object,
  position: PropTypes.number,
  onSelectQuickAction: PropTypes.func.isRequired,
};

const StyledQuickActionItemTextIE11 = overrideStyle('p')`
  position: relative;
  line-height: 1.4em;
  max-height: 2.8em;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const StyledQuickActionItemText = overrideStyle('p')`
  display: -webkit-box;
  color: black;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  line-height: 140%;
`;

const StyledQuickActionItemLink = overrideStyle('div')`
  display: flex;
  min-width: 19px;
  margin-left: 22px;
  justify-content: flex-end;
`;

const StyledQuickActionItem = overrideStyle('a')`
  align-items: center;
  border: 1px solid #DEDEDE;
  border-radius: ${p => getRadius(p.theme.borderRadius, 4, 8)};
  color: black;
  cursor: pointer;
  display: flex;
  font-family:${p => p.theme.fontLibrary.secondary};
  font-size: 14px;
  height: 20px;
  justify-content: space-between;
  letter-spacing: .3px;
  margin-bottom: 22px;
  outline: none;
  padding: 22px 18px;
  text-decoration: none;
  transition: border-color 200ms cubic-bezier(0.645, 0.045, 0.355, 1);
  &:hover, &:focus {
    border-color: ${shiftColorShade('#DEDEDE', -10)};
  }
  &:visited {
    color: black;
  }
`;

const mapDispatchToProps = dispatch => {
  return {
    onSelectQuickAction: ({ entityId, entityType, label, position }) => {
      dispatch(trackQuickActionSelected({ entityId: entityId, entityType, label, position }));
      if (entityType === 'answer') {
        dispatch(updateSelectedAnswer(entityId, label));
      }
    },
  };
};

export { QuickActionItem };
export default connect(
  null,
  mapDispatchToProps
)(QuickActionItem);
